import { CallCenterStats } from '@/api/callcenter/dashboard';
import { Card } from '@/components/ui/elements/card';
import { Heading2 } from '@/components/ui/typography/Typography';
import { formatDurationSeconds } from '@/features/callcenter/logic';
import { cn } from '@/utils/format';

type StatsBarProps = {
  stats: CallCenterStats | undefined;
};

type ResolvedTile = {
  value: string;
  valueClassName?: string;
};

type TileSpec = {
  label: string;
  resolve: (stats: CallCenterStats) => ResolvedTile;
};

const PLACEHOLDER = '—';

const formatSpeedToLead = (seconds: number | null): string =>
  seconds === null ? PLACEHOLDER : formatDurationSeconds(seconds);

const formatCloseRate = (rate: number | null): string =>
  rate === null ? PLACEHOLDER : `${Math.round(rate * 1000) / 10}%`;

// The six integer fields are never null per the API contract -- they're
// rendered directly via String(), with no `value || PLACEHOLDER` fallback,
// so a real 0 always displays as "0" rather than being mistaken for missing
// data. Only the two nullable fields (speed to lead, close rate) get a null
// guard. `resolve` only ever runs once `stats` is known to exist -- while
// `stats` itself is undefined (first load), StatsBar below renders a pulse
// block per tile instead of calling resolve, so there is no PLACEHOLDER path
// for a wholly-missing `stats` object anymore.
const TILE_SPECS: TileSpec[] = [
  {
    label: 'Speed to lead (today)',
    resolve: (stats) => ({ value: formatSpeedToLead(stats.speed_to_lead_avg_seconds_today) }),
  },
  {
    label: 'Leads called',
    resolve: (stats) => ({ value: String(stats.leads_called_today) }),
  },
  {
    label: 'SLA hits',
    resolve: (stats) => ({ value: String(stats.sla_hits_today) }),
  },
  {
    label: 'SLA breaches',
    resolve: (stats) => ({
      value: String(stats.sla_breaches_today),
      valueClassName: stats.sla_breaches_today > 0 ? 'text-destructive' : undefined,
    }),
  },
  {
    label: 'Calls',
    resolve: (stats) => ({ value: String(stats.calls_today) }),
  },
  {
    label: 'Conversations',
    resolve: (stats) => ({ value: String(stats.conversations_today) }),
  },
  {
    label: 'Leads completed',
    resolve: (stats) => ({ value: String(stats.leads_completed_today) }),
  },
  {
    label: 'Close rate (30d)',
    resolve: (stats) => ({ value: formatCloseRate(stats.close_rate_30d) }),
  },
];

export const StatsBar = ({ stats }: StatsBarProps) => {
  return (
    <div className="space-y-1">
      {/* Personal scope, labeled: the numbers come from AgentStatsService::forAgent,
          and without a header agents read them as team stats. */}
      <Heading2>Your stats</Heading2>
      <div
        data-testid="stats-bar"
        className="grid grid-cols-2 @lg:grid-cols-4 @4xl:grid-cols-8 gap-2"
      >
        {TILE_SPECS.map(({ label, resolve }) => {
          const resolved = stats ? resolve(stats) : null;

          return (
            <Card key={label} className="p-3">
              <div className="text-xs text-muted-foreground">{label}</div>
              {resolved ? (
                <div className={cn('text-xl font-semibold tabular-nums', resolved.valueClassName)}>
                  {resolved.value}
                </div>
              ) : (
                <div
                  data-testid="stat-value-skeleton"
                  className="mt-1 h-6 w-12 animate-pulse rounded bg-muted"
                />
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
};
